import { t } from "i18next";

import { VehicleType } from "@/src/types/types";
import { injectStructuredData as injectJsonLdScript } from "@/src/utils/seo";
import { createBaanaOrganization } from "@/src/utils/seo/builders";
import { StructuredDataOrganization } from "@/src/utils/seo/types";

const VEHICLE_HOME_PATH: Record<VehicleType, string> = {
  [VehicleType.CAR]: "/",
  [VehicleType.MOTO]: "/moto",
  [VehicleType.CARAVAN]: "/caravan"
};

type HomePageSeoKey = "car" | "moto" | "caravan";

const getHomePageSeoKey = (vehicleType: VehicleType): HomePageSeoKey => {
  switch (vehicleType) {
    case VehicleType.MOTO:
      return "moto";
    case VehicleType.CARAVAN:
      return "caravan";
    default:
      return "car";
  }
};

const getHomePageSeoTitle = (vehicleType: VehicleType): string =>
  t(`consumer.seo.homePage.title.${getHomePageSeoKey(vehicleType)}`);

const getHomePageSeoDescription = (vehicleType: VehicleType): string =>
  t(`consumer.seo.homePage.description.${getHomePageSeoKey(vehicleType)}`);

const getHomePageUrl = (vehicleType: VehicleType): string => {
  const path = VEHICLE_HOME_PATH[vehicleType];
  return path === "/"
    ? "https://baana.nettiauto.com/"
    : `https://baana.nettiauto.com${path}`;
};

// Structured Data Types

interface StructuredDataWebSite {
  "@type": "WebSite";
  "@id": string;
  url: string;
  name: string;
  alternateName?: string;
  description?: string;
  publisher: { "@id": string };
  inLanguage: string;
}

interface StructuredDataWebPage {
  "@type": "WebPage";
  "@id": string;
  url: string;
  name: string;
  isPartOf: { "@id": string };
  about: { "@id": string };
  primaryImageOfPage: ImageObject;
  datePublished: string;
  dateModified: string;
}

interface StructuredDataService {
  "@type": "Service";
  serviceType: string;
  name: string;
  description: string;
  provider: { "@id": string };
  areaServed: Country;
  audience: Audience;
  hasOfferCatalog: OfferCatalog;
}

interface StructuredDataFAQPage {
  "@type": "FAQPage";
  mainEntity: StructuredFAQQuestion[];
}

type StructuredDataGraph =
  | StructuredDataOrganization
  | StructuredDataWebSite
  | StructuredDataWebPage
  | StructuredDataService
  | StructuredDataFAQPage;

interface ImageObject {
  "@type": string;
  url: string;
  width?: string;
  height?: string;
}

interface Country {
  "@type": string;
  name: string;
}

interface Audience {
  "@type": string;
  audienceType: string;
  geographicArea: Country;
}

interface OfferCatalog {
  "@type": string;
  name: string;
  description: string;
  itemListElement: Offer[];
}

interface Offer {
  "@type": string;
  itemOffered: {
    "@type": string;
    name: string;
  };
  priceSpecification: UnitPriceSpecification;
}

interface UnitPriceSpecification {
  "@type": string;
  price: string;
  priceCurrency: string;
  unitText: string;
}

interface StructuredFAQQuestion {
  "@type": string;
  name: string;
  acceptedAnswer: StructuredFAQAnswer;
}

interface StructuredFAQAnswer {
  "@type": string;
  text: string;
}

interface StructuredData {
  "@context": string;
  "@graph": StructuredDataGraph[];
}

/**
 * Creates Organization structured data
 */
const createOrganizationData = (): StructuredDataOrganization => {
  return createBaanaOrganization();
};

/**
 * Creates WebSite structured data
 */
const createWebSiteData = (): StructuredDataWebSite => ({
  "@type": "WebSite",
  "@id": "https://baana.nettiauto.com/#website",
  url: "https://baana.nettiauto.com",
  name: "Baana – Kilpailuta auton tai moottoripyörän myynti liikkeeseen",
  alternateName: "Nettiauto",
  description:
    "Maksuton kilpailutuspalvelu autojen, moottoripyörien ja muiden ajoneuvojen myyntiin liikkeille. Kilpailuta myös moottoripyöräsi myynti vaivattomasti.",
  publisher: {
    "@id": "https://baana.nettiauto.com/#organization"
  },
  inLanguage: "fi"
});

/**
 * Creates WebPage structured data
 */
const createWebPageData = (
  vehicleType: VehicleType = VehicleType.CAR
): StructuredDataWebPage => {
  const path = VEHICLE_HOME_PATH[vehicleType];
  const pageId =
    path === "/"
      ? "https://baana.nettiauto.com/#webpage"
      : `https://baana.nettiauto.com${path}#webpage`;

  return {
    "@type": "WebPage",
    "@id": pageId,
    url: getHomePageUrl(vehicleType),
    name: getHomePageSeoTitle(vehicleType),
    isPartOf: {
      "@id": "https://baana.nettiauto.com/#website"
    },
    about: {
      "@id": "https://baana.nettiauto.com/#organization"
    },
    primaryImageOfPage: {
      "@type": "ImageObject",
      url:
        import.meta.env.VITE_BAANA_LOGO_URL ??
        "https://baana-frontpage-prod.s3.eu-west-1.amazonaws.com/images/Baana_by_nettiauto.webp"
    },
    datePublished: "2023-01-01",
    dateModified: "2025-10-14"
  };
};

/**
 * Creates Service structured data
 */
const createServiceData = (
  vehicleType: VehicleType = VehicleType.CAR
): StructuredDataService => ({
  "@type": "Service",
  serviceType:
    "Autojen, moottoripyörien ja karavaanien myynnin kilpailutuspalvelu",
  name: getHomePageSeoTitle(vehicleType),
  description: getHomePageSeoDescription(vehicleType),
  provider: {
    "@id": "https://baana.nettiauto.com/#organization"
  },
  areaServed: {
    "@type": "Country",
    name: "Finland"
  },
  audience: {
    "@type": "Audience",
    audienceType:
      "Auton, moottoripyörän ja karavaanin myyjät, jotka haluavat parhaan hinnan ja vaivattoman myyntikokemuksen",
    geographicArea: {
      "@type": "Country",
      name: "Finland"
    }
  },
  hasOfferCatalog: {
    "@type": "OfferCatalog",
    name: "Baanan palvelutarjonta",
    description:
      "Maksuton kilpailutuspalvelu auton, moottoripyörän tai karavaanin myyntiin liikkeille.",
    itemListElement: [
      {
        "@type": "Offer",
        itemOffered: {
          "@type": "Service",
          name: "Maksuton oman auton, moottoripyörän tai karavaanin kilpailutus"
        },
        priceSpecification: {
          "@type": "UnitPriceSpecification",
          price: "0",
          priceCurrency: "EUR",
          unitText: "Palvelua kohden"
        }
      }
    ]
  }
});

/**
 * Creates FAQPage structured data
 */
const createFAQPageData = (): StructuredDataFAQPage => ({
  "@type": "FAQPage",
  mainEntity: createFAQStructuredData()
});

/**
 * Creates HomePage structured data
 */
const createHomePageStructuredData = (
  vehicleType: VehicleType = VehicleType.CAR
): StructuredData => ({
  "@context": "https://schema.org",
  "@graph": [
    createOrganizationData(),
    createWebSiteData(),
    createWebPageData(vehicleType),
    createServiceData(vehicleType),
    createFAQPageData()
  ]
});

const createFAQStructuredData = (): StructuredFAQQuestion[] => [
  {
    "@type": "Question",
    name: "Paljonko palvelun käyttö maksaa?",
    acceptedAnswer: {
      "@type": "Answer",
      text: "Baana on yksityiskäyttäjälle maksuton palvelu. Kun myyt autosi, moottoripyöräsi tai muun ajoneuvon liikkeelle Baanan kilpailutuksen kautta, perimme ostajaliikkeeltä provision."
    }
  },
  {
    "@type": "Question",
    name: "Miten voin tietää ajoneuvoni arvon?",
    acceptedAnswer: {
      "@type": "Answer",
      text: "Baanassa voit saada autostasi hinta-arvion, joka perustuu keskihintoihin. Koska jokainen auto on yksilö, voi arvio erota todellisesta arvosta. Muista ajoneuvoista kuin autosta ei ole saatavilla hinta-arviota. Parhaan arvion käyvästä myyntihinnasta saat kilpailutuksesta, kun korkein tarjous on se, mitä liikkeet enimmillään ovat valmiita maksamaan. Ajoneuvojen kulumat ja renkaiden kunto vaikuttavat ostohintaan, hyvät lisävarusteet voivat nostaa arvoa."
    }
  },
  {
    "@type": "Question",
    name: "Miksi ajoneuvon myynti liikkeelle Baanan kautta kannattaa?",
    acceptedAnswer: {
      "@type": "Answer",
      text: "Kilpailutus Baanan kautta auttaa sinua saamaan parhaan hinnan ajoneuvon myynnistä liikkeelle. Auton myyminen autoliikkeelle tai kaksipyöräisen myyminen motoliikkeelle on tehokas tapa tehdä kaupat nopeasti ja vaivattomasti. Liike on luotettava ja kokenut ostaja. Se osaa arvioida asiantuntevasti ajoneuvosi ja hoitaa kaupan paperityöt."
    }
  },
  {
    "@type": "Question",
    name: "Miksi en myisi ajoneuvoani itse?",
    acceptedAnswer: {
      "@type": "Answer",
      text: "Itsemyynti on yksi vaihtoehto. Se voi kuitenkin viedä aikaa, ja yksityishenkilöiden välisessä kaupassa rahoituksen järjestäminen on monesti hankalaa. Jos kuitenkin haluat kokeilla, voit aloittaa itsemyynnin Nettiautossa."
    }
  },
  {
    "@type": "Question",
    name: "Onko ilmoituksen jättäminen vaikeaa?",
    acceptedAnswer: {
      "@type": "Answer",
      text: "Ei. Palvelumme opastaa ilmoituksenjätön kaikissa kohdissa ja kertoo, mitä tietoja kannattaa lisätä ja millaisia kuvia ilmoitukseen tarvitaan. Ilmoituksen jättäminen vie alle kymmenen minuuttia."
    }
  },
  {
    "@type": "Question",
    name: "Kauanko ajoneuvon myynnin kilpailutus kestää?",
    acceptedAnswer: {
      "@type": "Answer",
      text: "Tarjousten tekemisaika päättyy kahden arkipäivän kuluttua ilmoituksen julkaisusta klo 10–17 välillä. Näet päättymisajan, kun kilpailutuksesi on julkaistu. Tyypillisesti ajoneuvon myynnin kilpailutus Baanassa kestää valinnastasi riippuen muutamasta tunnista noin 48 tuntiin. Pikakilpailutuksella jopa samana päivänä."
    }
  },
  {
    "@type": "Question",
    name: "Sitoudunko tekemään kaupat, kun kilpailutan ajoneuvon myynnin?",
    acceptedAnswer: {
      "@type": "Answer",
      text: "Baanan käyttö ei velvoita sinua mihinkään. Jos et ole valmis myymään ajoneuvoasi kahden viikon kuluessa, suosittelemme sinua tekemään ilmoituksen vasta sitten, kun myyminen on ajankohtaista. Voit myös aina neuvotella korkeimman tarjouksen tehneen liikkeen kanssa tarjouksen hyväksymisen sijaan."
    }
  },
  {
    "@type": "Question",
    name: "Onko rahoitetun ajoneuvon myynti liikkeelle mahdollista?",
    acceptedAnswer: {
      "@type": "Answer",
      text: "Tottahan toki. Rahoitusyhtiön ajoneuvon myyminen onnistuu yhtä helposti kuin itse omistamasi. Liike vähentää myyntisummastasi loppuvelan, eikä sinun ei tarvitse huolehtia siitä. Mikäli loppuvelka on myyntihintaa suurempi, voit maksaa ylijäävän osuuden suoraan liikkeelle. Osa liikkeistä pystyy myös rahoittamaan jäljelle jäävän osuuden."
    }
  },
  {
    "@type": "Question",
    name: "Miten liikkeet kiinnostuisivat ajoneuvostani enemmän?",
    acceptedAnswer: {
      "@type": "Answer",
      text: "Liikkeet arvostavat ilmoituksia, joista he pystyvät helposti laskemaan ajoneuvosi arvon. Ajoneuvoja ostetaan, joiden kunto ja tiedot on kerrottu mahdollisimman kattavasti. Jos ajoneuvossasi on vaurioita, lisää niistä kuva. Myös selkeät kuvat ja tarkat tiedot esim. huoltokirjasta nostavat monesti tarjottavaa hintaa."
    }
  },
  {
    "@type": "Question",
    name: "Mitkä kaikki liikkeet ostavat ajoneuvoja Baanan kautta?",
    acceptedAnswer: {
      "@type": "Answer",
      text: "Lähes kaikki Suomen suurimmat liikkeet ovat palvelussa. Lisäksi mukana on paljon pienempiä liikkeitä."
    }
  },
  {
    "@type": "Question",
    name: "Ilmoituksen julkaiseminen ei onnistu tai olen tehnyt virheellisen ilmoituksen, mitä teen?",
    acceptedAnswer: {
      "@type": "Answer",
      text: "Suosittelemme kokeilemaan selaimen evästeiden poistoa. Jos se ei auta tai kohtaat muita ongelmia ilmoituksen suhteen, asiakastukemme auttaa mielellään puhelimitse 0600 04070, avoinna arkisin 9-16, la-su 11-15 (0,38€ /min + pvm) tai sähköpostitse asiakaspalvelu@almamobility.fi."
    }
  },
  {
    "@type": "Question",
    name: "Miten arvioin renkaideni kunnon?",
    acceptedAnswer: {
      "@type": "Answer",
      text: "Perinteinen tapa testata renkaiden kunto on asettaa kahden euron kolikko renkaan uraan. Hopeareunuksen leveys on noin 4 mm, joten sen avulla voi arvioida urien syvyyden. Tarkista eri ajoneuvojen sallitut urasyvyydet. Voit myös ottaa ilmoitukseen lähikuvat renkaistasi."
    }
  },
  {
    "@type": "Question",
    name: "Miten arvioin tuulilasin kunnon?",
    acceptedAnswer: {
      "@type": "Answer",
      text: "Monesti tuulilasiin tulee kiveniskemiä tai se voi haljeta. Tällaiset vauriot eivät jää huomaamatta. Silmämääräinen arviointi tuulilasin kunnosta on varmasti riittävä."
    }
  },
  {
    "@type": "Question",
    name: "Kannattaako minun korjata/huoltaa ajoneuvoni ennen myyntiä?",
    acceptedAnswer: {
      "@type": "Answer",
      text: "Ajoneuvon korjaaminen varmasti nostaa arvoa. On kuitenkin tapauskohtaista, nostaako korjauksen arvo hintaa enemmän kuin mitä korjaus kustantaa. Ostajaliikkeillä on usein hyvät yhteistyökumppanit korjaamiseen, joten voit jättää sen ostajan huoleksi. Muista vain mainita korjausta tarvitsevasta viasta ilmoituksessasi."
    }
  },
  {
    "@type": "Question",
    name: "Mitä tarkoittaa kohta ”kuinka pitkälle olet valmis toimittamaan ajoneuvon”?",
    acceptedAnswer: {
      "@type": "Answer",
      text: "Ajoneuvon luovutus kauppojen jälkeen voidaan tehdä joko niin, että ajoneuvo noudetaan, tai niin, että toimitat sen itse liikkeeseen. Tämä tieto on ostajaliikkeille, jotta he osaavat varautua mahdollisiin noutokustannuksiin. Mikäli olet valmis itse toimittamaan ajoneuvon, saat todennäköisesti siitä hieman paremman hinnan."
    }
  },
  {
    "@type": "Question",
    name: "Miten otan kuvat palveluun?",
    acceptedAnswer: {
      "@type": "Answer",
      text: "Älypuhelimen kamera riittää. Palvelu kertoo, mistä kohdista ajoneuvoa tarvitaan kuvat ja neuvoo niiden ottamisessa. Jos ajoneuvossasi on jotain erityistä, kuten vaurioita, suosittelemme ottamaan lisäkuvia. Mitä kattavammin ajoneuvo on kuvattu, sitä paremman käsityksen ostajaliike siitä saa."
    }
  },
  {
    "@type": "Question",
    name: "Voinko jättää ilmoituksen täytön kesken?",
    acceptedAnswer: {
      "@type": "Answer",
      text: "Kyllä, voit tallentaa keskeneräisen ilmoituksen ja jatkaa sen täyttöä, kun sinulle parhaiten sopii."
    }
  },
  {
    "@type": "Question",
    name: "Mitä tapahtuu, kun olen hyväksynyt autoliikkeen tarjouksen?",
    acceptedAnswer: {
      "@type": "Answer",
      text: "Liike saa yhteystietosi, ja on sinuun yhteydessä yleensä vuorokauden sisällä. Sovitte autoliikkeen kanssa suoraan, miten luovutus ja maksu hoidetaan. Kauppojen syntymisen jälkeen luovutuksesta tulee sopia mahdollisimman pian."
    }
  },
  {
    "@type": "Question",
    name: "Mitä jos en ole tyytyväinen saamiini tarjouksiin?",
    acceptedAnswer: {
      "@type": "Answer",
      text: "Jos et ole valmis hyväksymään mitään saamistasi tarjouksista, suosittelemme käyttämään neuvottelutoimintoa. Aloittamalla neuvottelun saat yhteyden liikkeeseen, ja voit ehdottaa hintaa, jolla olisit valmis myymään ajoneuvosi."
    }
  },
  {
    "@type": "Question",
    name: "Kannattaako uudehkon ajoneuvon myynti liikkeelle?",
    acceptedAnswer: {
      "@type": "Answer",
      text: "Ehdottomasti. Uudehkon ajoneuvon myynti Baanan kautta kannattaa, sillä ne ovat usein kaikkein kiinnostavimpia. Parhaimmillaan useampi liike on kiinnostunut juuri sinun ajoneuvostasi, ja kilpailutuksen myötä sinun saamasi hinta nousee."
    }
  },
  {
    "@type": "Question",
    name: "Mitä eroa on vaihtohyvityksellä ja välirahalla? / Voinko antaa ajoneuvon vaihdossa liikkeelle myymällä sen Baanan kautta?",
    acceptedAnswer: {
      "@type": "Answer",
      text: "Helpoimmalla pääset, kun myyt ajoneuvosi Baanan kautta ja käytät nämä rahat uuden ajoneuvon ostoon. Yleensä parhaimpaan lopputulokseen päädytään, kun vanha ajoneuvo myydään ja uusi ostetaan erikseen. Tällöin saat vanhasta ajoneuvostasi hyvän pääoman uuden ostoon."
    }
  },
  {
    "@type": "Question",
    name: "Miksi kilpailutusaika oli jo päättynyt, vaikka kellonaika näytti muuta?",
    acceptedAnswer: {
      "@type": "Answer",
      text: "Tietoliikennerajoituksista johtuen käyttöliittymässä näkyvä kellonaika voi heittää sekunnin suuntaansa. Suosittelemme olemaan jättämättä viestejä tai tarjouksia viimeisille sekunneille."
    }
  },
  {
    "@type": "Question",
    name: "Miksi kilpailutuksen kellonaika ei täsmää laitteeni kellonajan kanssa?",
    acceptedAnswer: {
      "@type": "Answer",
      text: "Käytämme kilpailutuksissa ja neuvotteluissa palvelumme virallista aikaa, jotta kellonaika on sama kaikille osapuolille, ja riippumaton päätelaitteiden päivämäärä- ja aika-asetuksista."
    }
  },
  {
    "@type": "Question",
    name: "Laitetaanko autoon paremmat renkaat eteen vai taakse?",
    acceptedAnswer: {
      "@type": "Answer",
      text: "Auton renkaista paremmat kannattaa laittaa takarenkaiksi. Jos rengaskoko on sama, renkaita on hyvä vaihdella kausivaihdon yhteydessä, jotta ne kuluvat tasaisesti."
    }
  },
  {
    "@type": "Question",
    name: "Mikä on kesärenkaan kulutuspinnan pienin sallittu urasyvyys?",
    acceptedAnswer: {
      "@type": "Answer",
      text: "Lain mukaan kesärenkaiden kulutuspinnan urasyvyyden on oltava vähintään 1,6 mm."
    }
  },
  {
    "@type": "Question",
    name: "Mikä on talvirenkaan kulutuspinnan pienin sallittu urasyvyys?",
    acceptedAnswer: {
      "@type": "Answer",
      text: "Lain mukaan talvirenkaan pienin sallittu urasyvyys renkaan pääurissa on 3 mm."
    }
  },
  {
    "@type": "Question",
    name: "Mistä löytyy auton sähköinen huoltokirja?",
    acceptedAnswer: {
      "@type": "Answer",
      text: "Auton sähköinen huoltokirja on tallennettu autovalmistajan omaan järjestelmään. Jos asioit muualla kuin merkkiliikkeessä, tarkista, että liike päivittää sen. Sähköisestä huoltokirjasta saa pyytäessä ajantasaisen otteen huollon yhteydessä."
    }
  },
  {
    "@type": "Question",
    name: "Auton huoltokirja hävinnyt - mitä tehdä?",
    acceptedAnswer: {
      "@type": "Answer",
      text: "Paperinen huoltokirja voi kadota. Varaudu siihen, että hävinnyt huoltokirja voi vaikuttaa jälleenmyyntiin ja pyyntihintaan, jos tehtyjä huoltoja ja auton historiaa ei muuten saada todennettua."
    }
  },
  {
    "@type": "Question",
    name: "Saako kuolinpesän autolla ajaa?",
    acceptedAnswer: {
      "@type": "Answer",
      text: "Kyllä, kuolinpesän auton käyttö on luvallista. Kuolinpesä voi lisätä pääasiallisen käyttäjän ajoneuvon haltijaksi, jolloin haltija voi itse vakuuttaa ajoneuvon."
    }
  },
  {
    "@type": "Question",
    name: "Kuolinpesän auton myynti ja verotus – maksaako kuolinpesä autokaupan voitosta veroa?",
    acceptedAnswer: {
      "@type": "Answer",
      text: "Kyllä, kuolinpesän auton myynnistä saatava voitto on veronalaista tuloa."
    }
  },
  {
    "@type": "Question",
    name: "Miten kuolinpesän auto myydään?",
    acceptedAnswer: {
      "@type": "Answer",
      text: "Kuolinpesän auton voi myydä Baanassa samoin kuin omassa omistuksessa olevan auton. Erona on, että omistajuuden siirtovaihetta ei voi tehdä sähköisesti, sillä kuolinpesä ei voi tunnistautua. Muutoksiin tarvitaan yhden kuolinpesän osakkaan allekirjoitus."
    }
  },
  {
    "@type": "Question",
    name: "Miten myydä rahoitusyhtiön auto?",
    acceptedAnswer: {
      "@type": "Answer",
      text: "Rahoitusyhtiön omistaman auton voi myydä Baanassa aivan samalla tavalla kuin itse omistamasi. Liike maksaa loppuvelan rahoitusyhtiölle ja hoitaa paperityöt. Loppuvelan määrä vähennetään kauppasummastasi."
    }
  },
  {
    "@type": "Question",
    name: "Voiko auton haltija myydä auton?",
    acceptedAnswer: {
      "@type": "Answer",
      text: "Kyllä, auton haltija voi myydä auton. Kun rahoitusyhtiö omistaa auton, voit haltijana kilpailuttaa ostotarjoukset Baanassa. Ostajana toimiva autoliike hoitaa loppuvelan kuittaamisen ja omistuksen siirron. Laina vähennetään sinulle maksettavasta kauppasummasta."
    }
  },
  {
    "@type": "Question",
    name: "Voiko auton myydä vaikka rahoitusta on jäljellä?",
    acceptedAnswer: {
      "@type": "Answer",
      text: "Auton voi myydä, vaikka siitä olisi rahoitusta jäljellä. Baanan kautta myydessä liike hoitaa jäljellä olevan lainan maksun rahoitusyhtiölle ja siirtää omistuksen omiin nimiinsä. Loppuvelka vähennetään kauppasummasta, jonka liike sinulle maksaa."
    }
  },
  {
    "@type": "Question",
    name: "Mitä, jos ajoneuvon loppuvelka on suurempi kuin ajoneuvon arvo?",
    acceptedAnswer: {
      "@type": "Answer",
      text: "Vaikka loppuvelan määrä ylittää ajoneuvon arvon, voit silti myydä ajoneuvosi Baanassa. Voit tarvittaessa kilpailuttaa lainatarjouksen loppuvelan maksuun."
    }
  },
  {
    "@type": "Question",
    name: "Milloin näen tulleet tarjoukset?",
    acceptedAnswer: {
      "@type": "Answer",
      text: "Tulleet tarjoukset hintoineen näkee vasta, kun huutoaika on kokonaisuudessaan päättynyt. Käythän kilpailutuksen päätyttyä hyväksymässä haluamasi tarjouksen tai mikäli et ole tyytyväinen tarjottuun hintaan, suosittelemme ehdottomasti aloittamaan neuvottelun."
    }
  }
];

/**
 * Injects HomePage structured data as JSON-LD script
 */
const injectHomePageJsonLd = (homePageJsonLd: StructuredData): void => {
  injectJsonLdScript(homePageJsonLd, "baana-injected-homepage-data");
};

/**
 * Creates and injects HomePage structured data
 */
const injectDefaultHomePageJsonLd = (): void => {
  const structuredData = createHomePageStructuredData();
  injectHomePageJsonLd(structuredData);
};

export {
  createHomePageStructuredData,
  getHomePageSeoDescription,
  getHomePageSeoTitle,
  injectHomePageJsonLd,
  injectDefaultHomePageJsonLd
};
